/**
 * Whether a class can have attendance taken at all.
 *
 * Attendance belongs to a schedule block, not to a class: a school might take
 * it once at homeroom and nowhere else, so most classes legitimately have it
 * switched off. A report that just renders zeroes for such a class reads as
 * broken data rather than as a deliberate setting, which is why the page says
 * which of these it is.
 */

export type AttendanceAvailability = 'enabled' | 'noSchedule' | 'notEnabled';

export interface BlockWithAttendance {
  id?: string;
  displayTitle?: string | null;
  attendanceEnabled?: boolean | null;
}

export function attendanceAvailability(
  blocks: ReadonlyArray<BlockWithAttendance> | null | undefined
): AttendanceAvailability {
  const list = blocks ?? [];
  if (list.length === 0) return 'noSchedule';
  return list.some((block) => block.attendanceEnabled) ? 'enabled' : 'notEnabled';
}

export interface AvailabilityCopy {
  title: string;
  detail: string;
  /** What to do next, phrased for whoever is reading it. */
  action: string;
}

/**
 * Teachers cannot edit schedule blocks — that is school setup — so they are
 * told who can, rather than being shown a control that would refuse them.
 */
export function availabilityCopy(
  availability: Exclude<AttendanceAvailability, 'enabled'>,
  canManage: boolean
): AvailabilityCopy {
  if (availability === 'noSchedule') {
    return {
      title: 'No schedule for this class',
      detail:
        'Attendance is taken against a scheduled block, and this class does not meet on one yet.',
      action: canManage
        ? 'Add a schedule block for this class, then turn on attendance for it.'
        : 'An administrator needs to put this class on the schedule before attendance can be taken.',
    };
  }
  return {
    title: 'Attendance is off for this class',
    detail:
      'This class meets on a schedule, but none of its blocks record attendance. Many schools take attendance once at homeroom rather than in every period.',
    action: canManage
      ? "Turn on attendance for this class's schedule block to start recording it."
      : 'Ask an administrator to turn on attendance for this class if it should be recorded.',
  };
}
